import React, {
	useState,
	useEffect,
	useCallback,
	useMemo,
} from 'react';
import {
	Row,
	Col,
	Flex,
	Form,
	Drawer,
	Radio,
	Input,
	Typography,
} from 'antd';
import moment, { Moment } from 'moment/moment';
import sortBy from 'lodash.sortby';
import { Values } from '@shared/utils/objectEnums';
import {
	Currencies,
	FuelTypes,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import type { UserData } from '@api/utils/sequelize/calculateUserData';
import { VoyageOffHirePeriodReturnType } from '@api/utils/sequelize/getVoyageOffHirePeriods';
import Button from '@client/components/Button';
import {
	createOffHirePeriod,
	updateOffHirePeriod,
} from '@client/lib/api';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import CurrencyInput from '@client/components/CurrencyInput';
import DatePicker from '@client/components/DatePicker';
import Checkbox from '@client/components/Checkbox/Checkbox';
import TooltipIcon from '@client/components/TooltipIcon';
import showErrorNotification from '@client/utils/showErrorNotification';
import { rules } from '@client/utils/form';
import { MultiCurrencyValuesType } from '@client/components/MultiCurrencyAmount';
import PercentageInput from '@client/components/PercentageInput';
import Select from '@client/components/Select';
import useAttachments from '@client/utils/hooks/useAttachments';
import { useAuth } from '@client/lib/auth';
import styles from './styles/OffHireForm.module.css';
import OffHireBunkersTable from './OffHireBunkersTable';
import { TIME_ZONE_FORMAT_OPTIONS } from './ItineraryTab/components/PortAwareDatePicker';

const { UTC, LocalTime } = TimeZoneFormat;

type OffHireFormProps = {
	initialValues?: (Partial<{
		id: number;
		preferDuration: boolean;
		startTime: Moment;
		endTime: Moment;
		bunkers: Array<OffHireBunker> | [];
		hirePerDay: number | null;
		percentageForOwnersAccount: number;
		type: string | null;
	}> | VoyageOffHirePeriodReturnType) | null;
	voyageId: number;
	visible: boolean;
	hireDescription?: string;
	onClose: () => void;
	onSaved: () => void;
	currency: Values<typeof Currencies>;
};

export type OffHireBunkerValues = {
	id: number;
	quantity: number;
	fuelGrade: Values<typeof FuelTypes>;
	pricePerTon: MultiCurrencyValuesType;
};

type OffHireBunker = {
	id: number;
	quantity: number;
	pricePerTon: number;
	fuelGrade: Values<typeof FuelTypes>;
	exchangeRate: number;
	currency: Values<typeof Currencies>;
};

const dateProps = {
	time: true,
	fullWidth: true,
	defaultUtcOffset: 0,
	wrapperClassName: styles.dateTimePicker,
};

const OffHireForm = ({
	initialValues = null,
	voyageId,
	visible,
	hireDescription,
	onClose: forceOnClose,
	onSaved,
	currency,
}: OffHireFormProps) => {
	const [form] = Form.useForm();
	const [dirty, setDirty] = useState(false);
	const [saveLoading, setSaveLoading] = useState(false);
	const [bunkers, setBunkers] = useState<OffHireBunker[] | []>([]);
	const [editingBunkers, setEditingBunkers] = useState<number | null>(null);
	const [durationType, setDurationType] = useState(initialValues?.preferDuration ? 'duration' : 'date');
	const selectedTimeFormat = Form.useWatch('selectedTimeFormat', form);
	const { userInfo } = useAuth();

	const linkedAttachments = useMemo(() => [{
		label: 'Vessel',
		attachments: initialValues != null && 'vesselAttachments' in initialValues ?
			initialValues.vesselAttachments :
			[],
	}], [initialValues]);

	const isInvoiced = initialValues != null &&
		'HIIOffHirePeriod' in initialValues &&
		initialValues.HIIOffHirePeriod != null;

	const offHireTypes = useMemo(() => (userInfo as UserData)?.orgOffHireTypes, [userInfo]);

	const getDurationFromDates = useCallback((startTime: Moment, endTime: Moment) => {
		if (startTime == null || endTime == null) {
			return {};
		}

		const duration = moment.duration(toMoment(endTime).diff(startTime));

		return {
			'durationDays': duration.days(),
			'durationHours': duration.hours(),
			'durationMinutes': duration.minutes(),
		};
	}, []);

	const {
		useBlocker,
		makeBlockable,
	} = useNavigationBlock();

	const onClose = makeBlockable(forceOnClose);

	useBlocker(dirty || editingBunkers != null);

	const {
		uploaded,
		modal,
	} = useAttachments({
		attachments: initialValues != null && 'attachments' in initialValues ? initialValues.attachments : [],
		linkedAttachments,
		open: visible,
		setOpen: onClose,
		spacerWidth: 520,
		attachmentTypeLabel: 'off-hire',
	});

	useEffect(() => {
		if (!visible) {
			setDirty(false);
			form.resetFields();
			setBunkers([]);
			setEditingBunkers(null);
			setDurationType('date');
		} else if (initialValues != null) {
			let formValues = initialValues;

			if (initialValues?.startTime != null && initialValues?.endTime != null) {
				const durations = getDurationFromDates(
					toMoment(initialValues.startTime),
					toMoment(initialValues.endTime),
				);

				formValues = { ...formValues, ...durations };
			}

			form.setFieldsValue(formValues);
			setBunkers(initialValues.bunkers || []);
			setDurationType(initialValues.preferDuration ? 'duration' : 'date');
		}
	}, [form, visible, initialValues, getDurationFromDates]);

	useEffect(() => {
		if (editingBunkers == null) {
			return;
		}

		setDirty(true);
	}, [editingBunkers]);

	const saveOffHire = async ({
		startTime,
		endTime,
		hirePerDay,
		percentageForOwnersAccount,
		note,
		invoiceNote,
		type,
		useInvoiceTimeFormat,
	}: {
		startTime: Moment;
		endTime: Moment;
		hirePerDay: number;
		percentageForOwnersAccount: number;
		note: string;
		invoiceNote: string;
		type: string;
		useInvoiceTimeFormat: boolean;
	}) => {
		const preferDuration = durationType === 'duration';

		try {
			setSaveLoading(true);
			if (initialValues != null && initialValues.id != null) {
				await updateOffHirePeriod(
					voyageId,
					initialValues.id,
					{
						startTime,
						endTime,
						preferDuration,
						bunkers,
						hirePerDay,
						note,
						invoiceNote,
						percentageForOwnersAccount,
						type: type ?? null,
						selectedTimeFormat,
						useInvoiceTimeFormat,
					},
					uploaded,
				);
			} else {
				await createOffHirePeriod(
					voyageId,
					startTime,
					endTime,
					preferDuration,
					bunkers,
					hirePerDay,
					percentageForOwnersAccount,
					note,
					invoiceNote,
					type,
					selectedTimeFormat,
					useInvoiceTimeFormat,
					uploaded,
				);
			}

			forceOnClose();
			onSaved();
		} catch (e) {
			showErrorNotification('Could not save off-hire', e as Error);
		} finally {
			setSaveLoading(false);
		}
	};

	const setEndTimeFromDurations = useCallback((startTime: Moment) => {
		const days = form.getFieldValue('durationDays');
		const hours = form.getFieldValue('durationHours');
		const minutes = form.getFieldValue('durationMinutes');
		const endTimeOffset = toMoment(form.getFieldValue('endTime') ?? startTime).utcOffset();
		const endTime = toMoment(startTime)
			.add(days, 'days')
			.add(hours, 'hours')
			.add(minutes, 'minutes')
			.utcOffset(endTimeOffset);
		form.setFieldsValue({
			'endTime': endTime,
		});
	}, [form]);

	const handleDateChange = useCallback(() => {
		const startTime = form.getFieldValue('startTime');

		if (startTime == null) {
			return;
		}

		if (durationType === 'date') {
			const endTime = form.getFieldValue('endTime');

			if (endTime == null) {
				return;
			}

			const durations = getDurationFromDates(startTime, endTime);
			form.setFieldsValue(durations);
		} else {
			setEndTimeFromDurations(startTime);
		}
	}, [durationType, form, getDurationFromDates, setEndTimeFromDurations]);

	useEffect(() => {
		handleDateChange();
	}, [durationType, handleDateChange]);

	return (
		<>
			{modal}
			<Drawer
				title="Add off-hire"
				placement="left"
				onClose={onClose}
				open={visible}
				width={520}
				className={styles.drawer}
				mask={false}
			>
				<Form
					layout="vertical"
					form={form}
					onFinish={saveOffHire}
					onValuesChange={(changedValues) => {
						if (changedValues.selectedTimeFormat === UTC) {
							const { startTime, endTime } = form.getFieldsValue();

							form.setFieldsValue({
								startTime: startTime && toMoment(startTime).utc(),
								endTime: endTime && toMoment(endTime).utc(),
							});
						}

						handleDateChange();
						setDirty(true);
					}}
				>
					<Row
						gutter={16}
						className={styles.rowsWithLessMargin}
					>
						<Col span={24}>
							<Flex
								gap={16}
								align="flex-end"
							>
								<Form.Item
									name="selectedTimeFormat"
									label="Time format"
									initialValue={UTC}
									className={styles.timeFormatSelect}
								>
									<Select options={TIME_ZONE_FORMAT_OPTIONS} />
								</Form.Item>
								<Form.Item
									name="useInvoiceTimeFormat"
									valuePropName="checked"
									initialValue={false}
								>
									<Checkbox disabled={isInvoiced}>
										Use invoicing time format when invoicing
									</Checkbox>
								</Form.Item>
							</Flex>
						</Col>
						<Radio.Group
							onChange={({ target: { value } }) => setDurationType(value)}
							value={durationType}
						>
							<Col
								span={15}
								className={styles.dateRow}
							>
								<Form.Item
									name="startTime"
									label="From"
									rules={[rules.required]}
								>
									<DatePicker
										disableTimezone={selectedTimeFormat !== LocalTime}
										{...dateProps}
									/>
								</Form.Item>
							</Col>
							<Col
								span={24}
								className={styles.dateRow}
							>
								<Row
									align="middle"
									gutter={10}
								>
									<Col span={15}>
										<Form.Item
											name="endTime"
											label="To"
											rules={durationType === 'date' ? [rules.required] : []}
										>
											<DatePicker
												disabled={durationType !== 'date'}
												disableTimezone={selectedTimeFormat !== LocalTime}
												{...dateProps}
											/>
										</Form.Item>
									</Col>
									<Col
										span={1}
										className={styles.radioBtnCol}
									>
										<Radio
											value="date"
											className={styles.radioBtn}
										/>
									</Col>
									<Col className={styles.radioLabelCol} span={5}>
										<Typography.Text className={styles.radioLabel}>
											Use date
										</Typography.Text>
									</Col>
								</Row>
							</Col>
							<Col
								span={24}
							>
								<Row
									align="middle"
									gutter={10}
									className={styles.durationRow}
								>
									<Col span={5}>
										<Form.Item
											label="Days"
											initialValue={0}
											name="durationDays"
											rules={durationType === 'duration' ? [rules.requiredShort] : []}
											className={styles.durationEntryForm}
										>
											<Input
												disabled={durationType !== 'duration'}
											/>
										</Form.Item>
									</Col>
									<Col span={5}>
										<Form.Item
											label="Hours"
											initialValue={0}
											name="durationHours"
											rules={durationType === 'duration' ? [rules.requiredShort] : []}
											className={styles.durationEntryForm}
										>
											<Input
												disabled={durationType !== 'duration'}
											/>
										</Form.Item>
									</Col>
									<Col span={5}>
										<Form.Item
											label="Minutes"
											initialValue={0}
											name="durationMinutes"
											rules={durationType === 'duration' ? [rules.requiredShort] : []}
											className={styles.durationEntryForm}
										>
											<Input
												disabled={durationType !== 'duration'}
											/>
										</Form.Item>
									</Col>
									<Col
										span={1}
										className={styles.radioBtnCol}
									>
										<Radio
											value="duration"
											className={styles.radioBtn}
										/>
									</Col>
									<Col className={styles.radioLabelCol} span={5}>
										<Typography.Text className={styles.radioLabel}>
											Use duration
										</Typography.Text>
									</Col>
								</Row>
							</Col>
						</Radio.Group>
						<Col span={12}>
							<Form.Item
								name="hirePerDay"
								label={(
									<>
										Hire per day
										<TooltipIcon>
											The hire amount per day the
											off-hire period should be based on.
										</TooltipIcon>
									</>
								)}
								rules={[rules.required]}
							>
								<CurrencyInput
									placeholder={hireDescription}
									currency={currency}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="percentageForOwnersAccount"
								label="Proportion for owner's account"
								rules={[rules.required]}
							>
								<PercentageInput min={0} max={100} />
							</Form.Item>
						</Col>
					</Row>
					{
						((offHireTypes != null &&
						offHireTypes.length !== 0) ||
						initialValues?.type != null) && (
							<Row>
								<Col span={12}>
									<Form.Item
										name="type"
										label="Off-hire type"
									>
										<Select
											options={(sortBy(offHireTypes) ?? []).map((ot) => ({
												label: ot,
												value: ot,
											}))}
											allowClear
											placeholder="Select an off-hire type"
										/>
									</Form.Item>
								</Col>
							</Row>
						)
					}
					<Row gutter={16}>
						<Col span={24}>
							<OffHireBunkersTable
								key={`table-${bunkers.length}`}
								bunkers={bunkers}
								setBunkers={setBunkers}
								editingBunkers={editingBunkers}
								setEditingBunkers={setEditingBunkers}
								currency={currency}
								setDirty={setDirty}
							/>
						</Col>
					</Row>
					<Row className={styles.noteInputRow} gutter={16}>
						<Col
							span={24}
							className={styles.rowsWithLessMargin}
						>
							<Form.Item
								name="note"
								label="Internal note"
							>
								<Input.TextArea />
							</Form.Item>
						</Col>
					</Row>
					<Row className={styles.noteInputRow} gutter={16}>
						<Col span={24}>
							<Form.Item
								name="invoiceNote"
								label="Invoice note"
							>
								<Input.TextArea />
							</Form.Item>
						</Col>
					</Row>
					<Row justify="end" gutter={[0, 16]}>
						<Col
							className={styles.rowsWithLessMargin}
						>
							<Form.Item>
								{editingBunkers != null && (
									<span className={styles.bunkerSaveWarning}>
										Please confirm bunkers consumed before saving
									</span>
								)}
								<Button
									type="primary"
									htmlType="submit"
									disabled={editingBunkers != null}
									loading={saveLoading}
								>
									Save
								</Button>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Drawer>
		</>
	);
};

export default OffHireForm;
